import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { LoadingState, stringHelpers } from '@nocowanie/core';
import {
    AdaptiveDrawer,
    InfiniteScroll,
    scrollToSection,
    usePluralisationTranslation,
} from '@nocowanie/common-ui';

import { ReviewItem } from '@app/components/molecules/review-item';
import { ReviewSummary } from '@app/components/molecules/review-summary';
import { ReviewsHeader } from '@app/components/molecules/reviews-header';
import { ReviewsSearch } from '@app/components/molecules/reviews-search';
import { ReviewsSorting } from '@app/components/molecules/reviews-sorting';
import { StickyHeader } from '@app/components/molecules/sticky-header';
import { REVIEWS_MIN_QUERY_LENGTH } from '@app/consts';
import { AppModalsEnum, ReviewSortEnum } from '@app/enums';
import { useModalFragmentSync } from '@app/hooks';
import {
    accommodationRatingsSlice,
    accommodationStaticSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

import { ReviewsModalProps } from './reviews-modal.props';
import useDebounce from 'react-use/lib/useDebounce';

const ScrollTopButton = dynamic(
    () => import('@app/components/atoms/scroll-top-button').then(c => c.ScrollTopButton),
    { ssr: false },
);

export const ReviewsModal = ({
    reviewItemIndex,
    setReviewItemIndex,
    onDrawerOpen,
    fetchData,
}: ReviewsModalProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'reviews',
    });
    const scrollableRef = useRef<HTMLDivElement>(null);

    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Reviews),
    );
    const { ratingAverage, ratingCount } = useAppSelector(
        accommodationStaticSlice.selectors.selectReviewsData,
    );
    const sortBy = useAppSelector(accommodationRatingsSlice.selectors.selectSortBy);
    const query = useAppSelector(accommodationRatingsSlice.selectors.selectQuery);
    const [rawQuery, setRawQuery] = useState(query);
    const totalCount = useAppSelector(accommodationRatingsSlice.selectors.selectTotalCount);
    const isValidLength = !query.length || query.length >= REVIEWS_MIN_QUERY_LENGTH;
    const loadingState = useAppSelector(accommodationRatingsSlice.selectors.selectLoading);
    const isLoading = loadingState === LoadingState.PENDING;
    const isLoadingDone = [LoadingState.SUCCESS, LoadingState.ERROR].includes(loadingState);
    const ratings = useAppSelector(accommodationRatingsSlice.selectors.selectRatings);
    const hasMorePages = useAppSelector(accommodationRatingsSlice.selectors.selectHasPagesToLoad);
    const [currentValues, setCurrentValues] = useState({
        sortBy,
        query: query,
    });
    const isInitialDataLoaded = useAppSelector(
        accommodationRatingsSlice.selectors.selectIsInitialDataLoaded,
    );
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    useDebounce(
        () => {
            dispatch(accommodationRatingsSlice.actions.setQuery(rawQuery));
            setDebouncedQuery(rawQuery);
        },
        300,
        [rawQuery],
    );

    const closeDrawer = useCallback(() => {
        dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Reviews));
        if (sortBy !== ReviewSortEnum.MOST_HELPFUL || query.length) {
            dispatch(accommodationRatingsSlice.actions.resetData());
        }
        setRawQuery('');
        setShouldScrollToTarget(false);
        setReviewItemIndex && setReviewItemIndex(null);
    }, [dispatch, sortBy, query.length, setReviewItemIndex]);

    const onHashOpen = useCallback(() => {
        setTimeout(() => {
            if (!isInitialDataLoaded && !isLoading) {
                fetchData?.();
            }
        }, 0);
    }, [fetchData, isInitialDataLoaded, isLoading]);

    useModalFragmentSync(AppModalsEnum.Reviews, isDrawerOpen, onHashOpen);

    const targetRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);
    const [shouldScrollToTarget, setShouldScrollToTarget] = useState(false);

    const onNextPage = useCallback(() => {
        if ((isInitialDataLoaded && !hasMorePages) || !isValidLength) {
            return;
        }

        fetchData?.();
    }, [fetchData, hasMorePages, isInitialDataLoaded, isValidLength]);

    const onSortChange = useCallback((newSort: ReviewSortEnum) => {
        setTimeout(() => {
            dispatch(accommodationRatingsSlice.actions.setSortBy(newSort));
        }, 0);
    }, []);

    const onQueryChange = useCallback((query: string) => {
        setRawQuery(query);
    }, []);

    const encodedQuery = useMemo(() => {
        return stringHelpers.htmlEncode(debouncedQuery);
    }, [debouncedQuery]);

    useEffect(() => {
        const newValues = {
            sortBy,
            query: debouncedQuery,
        };

        if (isEqual(currentValues, newValues)) {
            return;
        }

        setCurrentValues({
            ...newValues,
        });

        if (!isValidLength || !isDrawerOpen) {
            return;
        }

        setTimeout(() => {
            fetchData?.();
        }, 0);
    }, [
        currentValues.query,
        currentValues.sortBy,
        debouncedQuery,
        fetchData,
        isDrawerOpen,
        isValidLength,
        sortBy,
    ]);

    const ratingItems = useMemo(() => {
        return ratings.map((item, index) => {
            return item ? (
                <ReviewItem
                    ref={reviewItemIndex === index ? targetRef : null}
                    review={item}
                    key={index}
                    highlightedQuery={encodedQuery}
                />
            ) : null;
        });
    }, [ratings, reviewItemIndex, encodedQuery]);

    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'reviews');

    const pluralizedHeader = useMemo(() => {
        if (!totalCount) {
            return '';
        }

        return t('search.resultsHeader', {
            value: pluralizeTranslation(totalCount, 'search.headerReview'),
            value2: encodedQuery,
        });
    }, [pluralizeTranslation, encodedQuery, t, totalCount]);

    useEffect(() => {
        if (!targetRef.current || !shouldScrollToTarget || !isInitialDataLoaded) {
            return;
        }

        setTimeout(() => {
            if (!isLoading) {
                scrollToSection(targetRef, scrollableRef, stickyRef);
                setShouldScrollToTarget(false);
            }
        }, 10);
    }, [shouldScrollToTarget, isInitialDataLoaded]);

    useEffect(() => {
        if (typeof reviewItemIndex === 'number') {
            setShouldScrollToTarget(true);
        }
    }, [reviewItemIndex]);

    return !ratingAverage || !ratingCount ? null : (
        <AdaptiveDrawer
            isOpen={isDrawerOpen}
            onOpen={onDrawerOpen}
            onClose={closeDrawer}
            drawerProps={{
                size: {
                    base: 'full',
                    md: 'md',
                },
                variant: isMobileBrowser ? 'withMargin' : undefined,
                placement: {
                    base: 'bottom',
                    md: 'end',
                },
            }}
            contentProps={{
                'data-testid': 'review-drawer',
            }}
            overflowY={'scroll'} // prevent width jumping
            bodyRef={scrollableRef}
            header={t('modal.header')}
            p={{ base: 4, md: 6 }}
            overflowX={'hidden'}
            mb={{
                base: 16,
                md: 0,
            }}
        >
            <ReviewsHeader mb={5} />

            <ReviewSummary withSidebar />

            <StickyHeader
                ref={stickyRef}
                scrollableRef={scrollableRef}
                isOpen={isDrawerOpen}
                as={Flex}
                css={{ mx: -6, pt: 6, pb: 3, px: 6, mb: 3 }}
                gap={4}
                flexDirection={'column'}
                data-testid={'review-drawer-sticky-bar'}
            >
                <ReviewsSearch query={rawQuery} onQueryChange={onQueryChange} />
                <ReviewsSorting currentSort={sortBy} onSortChange={onSortChange} mx={-6} />
            </StickyHeader>

            <InfiniteScroll
                onNextPage={onNextPage}
                isLoading={isLoading}
                hasMorePages={isValidLength && hasMorePages}
                errorMessage={t('modal.loadingError')}
                display={'grid'}
                gridTemplateColumns={'1fr'}
                gap={isMobileBrowser ? '1' : '2'}
            >
                {query && isValidLength && ratingItems.length && isLoadingDone ? (
                    <Text
                        data-testid={'review-search-header'}
                        fontWeight={'bold'}
                        dangerouslySetInnerHTML={{
                            __html: pluralizedHeader,
                        }}
                    />
                ) : null}
                {!isValidLength || (isValidLength && !ratingItems.length && isLoadingDone) ? (
                    <Box>
                        <Text
                            fontWeight={'bold'}
                            data-testid={
                                !isValidLength ? 'review-search-error' : 'review-search-no-results'
                            }
                            dangerouslySetInnerHTML={{
                                __html: !isValidLength
                                    ? t('search.minLengthInfo.header', {
                                          value: REVIEWS_MIN_QUERY_LENGTH,
                                      })
                                    : t('search.noResultsInfo.header', {
                                          value: encodedQuery,
                                      }),
                            }}
                        />
                        <Text
                            data-testid={
                                !isValidLength
                                    ? 'review-search-error-hint'
                                    : 'review-search-no-results-hint'
                            }
                        >
                            {!isValidLength
                                ? t('search.minLengthInfo.hint')
                                : t('search.noResultsInfo.hint')}
                        </Text>
                    </Box>
                ) : null}
                {isValidLength ? ratingItems : null}
            </InfiniteScroll>
            <ScrollTopButton
                scrollableRef={scrollableRef}
                present={!isLoading}
                data-testid={'reviews-drawer-scroll-to-top'}
            />
        </AdaptiveDrawer>
    );
};
